import React, { useMemo } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

const finePointer = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: fine)").matches;

/* ---- 3D tilt wrapper (hover follows cursor) ---- */
export const TiltCard = ({ children, className = "", max = 9, ...props }) => {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 150, damping: 14 });
  const sry = useSpring(ry, { stiffness: 150, damping: 14 });

  const onMove = (e) => {
    if (!finePointer()) return;
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * max * 2);
    rx.set(-py * max * 2);
  };
  const reset = () => { rx.set(0); ry.set(0); };

  return (
    <motion.div
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={{ rotateX: srx, rotateY: sry, transformPerspective: 1000, transformStyle: "preserve-3d" }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
};

/* ---- Floating cocoa particles rising inside a section ---- */
export const CocoaParticles = ({ count = 14 }) => {
  const dots = useMemo(
    () =>
      Array.from({ length: count }).map(() => ({
        left: Math.random() * 100,
        delay: Math.random() * 10,
        dur: 9 + Math.random() * 12,
        size: 3 + Math.random() * 9,
        op: 0.08 + Math.random() * 0.22,
      })),
    [count]
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden z-[2]" aria-hidden>
      {dots.map((d, i) => (
        <span
          key={i}
          className="cocoa-dot"
          style={{
            left: `${d.left}%`,
            width: d.size,
            height: d.size,
            opacity: d.op,
            animationDelay: `${d.delay}s`,
            animationDuration: `${d.dur}s`,
          }}
        />
      ))}
    </div>
  );
};

/* ---- 3D hero stage: layered brand renders with mouse parallax ---- */
export const HeroStage3D = ({ mx, my }) => {
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const rotY = useTransform(sx, (v) => -14 + v * 22);
  const rotX = useTransform(sy, (v) => 6 - v * 14);
  const backX = useTransform(sx, (v) => v * -40);
  const frontX = useTransform(sx, (v) => v * 60);
  const frontY = useTransform(sy, (v) => v * 40);
  return (
    <div className="relative h-[340px] sm:h-[460px] lg:h-[540px] [perspective:1400px]" aria-hidden data-testid="hero-3d-stage">
      <div className="absolute inset-[8%] rounded-full bg-[#D4AF37]/20 blur-[90px]" />
      <motion.div style={{ rotateY: rotY, rotateX: rotX, transformStyle: "preserve-3d" }} className="absolute inset-0">
        <motion.img src="/catalogue/decor/sphere-pool.webp" alt="" style={{ x: backX, translateZ: -120 }}
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 0.85, y: 0 }} transition={{ duration: 1.2, delay: 0.3 }}
          className="absolute left-0 top-[4%] w-[52%] rounded-3xl ring-1 ring-[#D4AF37]/30 shadow-2xl object-cover aspect-[4/3]" />
        <motion.img src="/catalogue/decor/hero-float.webp" alt="" style={{ translateZ: 40 }}
          initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }}
          className="absolute right-0 top-[16%] w-[78%] rounded-[28px] ring-1 ring-[#D4AF37]/50 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)] object-cover aspect-[16/10]" />
        <motion.img src="/catalogue/decor/gift-box.webp" alt="" style={{ x: frontX, y: frontY, translateZ: 160 }}
          initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, delay: 0.6 }}
          className="absolute left-[6%] bottom-[2%] w-[44%] rounded-2xl ring-2 ring-[#D4AF37]/60 shadow-2xl object-cover aspect-[4/3] animate-float" />
        <motion.img src="/brand/emblem.png" alt="" style={{ translateZ: 220 }}
          className="absolute right-[4%] bottom-[4%] w-20 sm:w-28 rounded-full ring-2 ring-[#D4AF37]/70 shadow-2xl animate-float-slow" />
      </motion.div>
    </div>
  );
};

/* ---- Rotating 3D ring of product renders ---- */
export const ProductRing3D = ({ items }) => {
  const n = items.length;
  const radius = typeof window !== "undefined" && window.innerWidth < 640 ? 260 : 460;
  return (
    <div className="relative h-[380px] sm:h-[460px] [perspective:1600px] overflow-hidden" data-testid="product-ring-3d">
      <div className="ring-spin absolute left-1/2 top-1/2 [transform-style:preserve-3d]">
        {items.map((p, i) => (
          <a key={p.product_id} href={`/product/${p.slug}`} data-testid={`ring-item-${p.slug}`}
            style={{ transform: `rotateY(${(360 / n) * i}deg) translateZ(${radius}px)` }}
            className="absolute -left-[90px] -top-[115px] w-[180px] [backface-visibility:hidden] group">
            <div className="lux-card overflow-hidden ring-1 ring-[#D4AF37]/30 group-hover:ring-[#D4AF37] transition-shadow">
              <img src={p.images?.[1] || p.images?.[0]} alt={p.name} loading="lazy" className="h-[150px] w-full object-cover" />
              <div className="p-3">
                <p className="text-[10px] tracking-widest text-[#8C7B6E]">{p.sku}</p>
                <p className="font-display text-sm text-[#FDFBF7] truncate">{p.name}</p>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

/* ---- Chocolate wave divider ---- */
export const ChocolateWave = ({ flip = false, color = "#120A07", className = "" }) => (
  <div className={`w-full overflow-hidden leading-[0] ${flip ? "rotate-180" : ""} ${className}`} aria-hidden>
    <svg viewBox="0 0 1200 70" preserveAspectRatio="none" className="w-full h-[46px] sm:h-[64px]">
      <path d="M0,38 C180,70 360,6 600,30 C840,54 1020,8 1200,36 L1200,70 L0,70 Z" fill={color} />
      <path d="M0,38 C180,70 360,6 600,30 C840,54 1020,8 1200,36" fill="none" stroke="#D4AF37" strokeOpacity="0.35" strokeWidth="2" />
    </svg>
  </div>
);
